import { Component, ErrorInfo, ReactNode } from 'react';
import Card from './ui/Card';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

/**
 * Global error boundary that catches React render errors.
 * Displays a friendly recovery page instead of a blank screen.
 */
export default class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null });
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      return (
        <main className="bg-slate-50 min-h-screen flex items-center justify-center px-4">
          <div className="max-w-md w-full">
            <Card>
              <div className="text-center space-y-4">
                <svg className="w-12 h-12 mx-auto text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                <h1 className="text-xl font-semibold text-slate-950">Something went wrong</h1>
                <p className="text-sm text-slate-600">
                  An unexpected error occurred. Your data has not been lost.
                </p>
                <div className="flex gap-3 justify-center pt-2">
                  <button onClick={this.handleRetry} className="btn-primary">
                    Try again
                  </button>
                  <button onClick={this.handleGoHome} className="btn-secondary">
                    Go to homepage
                  </button>
                </div>
              </div>
            </Card>
          </div>
        </main>
      );
    }

    return this.props.children;
  }
}
